import { AmendmentModel } from '@signalvote/shared';
import type { FilingTransport } from '@signalvote/shared';
import { useComplianceProfile, useFinanceSettings } from '@/hooks/queries/useFinanceSettings';
import { portalNameFor } from '@/components/finance/reports/amendmentWorkflowHint';
import { readySettings } from '@/lib/financeSettingsRead';
import { readyProfile } from '@/lib/complianceProfileRead';

/**
 * The one place the treasurer-facing "you upload to the portal, we don't file
 * for you" filing-model boundary is resolved, so every surface that renders it
 * (dashboard banners/captions, close masthead, inbox legend, ledger row chips)
 * agrees and none re-hardcodes "Alabama" / "FCPA portal" / `stateCode === 'AL'`.
 *
 * The boundary keys off the filing profile's SERVING-MODEL axis — the compliance
 * profile's `uploadIsFiling`. A jurisdiction where uploading the generated file
 * does NOT file it (`uploadIsFiling = false`: the treasurer uploads to the state
 * portal, then marks it filed) is a portal upload; federal in-system e-file
 * (`uploadIsFiling = true`, generate = file) is not. This is the correct axis:
 * it is TRUE for BOTH Alabama (append-only delta) and Virginia (whole-report
 * COMET upload), even though those two disagree on the amendment axis — VA's
 * `amendmentModel` is `SelfContainedUpload`, so an `amendmentModel === PortalOnly`
 * gate would mis-classify VA as non-portal. (This is why portal-upload keys on
 * `uploadIsFiling`, not the amendment axis.)
 *
 * The human jurisdiction name comes from the compliance profile
 * (`stateDisplayName`, e.g. "Alabama"; null for federal) — never derived from a
 * raw `stateCode`.
 */
export interface PortalUploadModel {
  /**
   * True when generating the filing does NOT file it — the treasurer gets the file into the
   * state's system themselves and then marks it filed (`uploadIsFiling === false`). This is the
   * sole gate for the "you upload / nothing is filed automatically" boundary copy. TRUE for every
   * jurisdiction today — AL, VA, NC, and federal (#482: download-and-self-upload to efo.fec.gov);
   * FALSE only for a rules-only ruleset (`uploadIsFiling` null) or before the profile loads.
   */
  isPortalUpload: boolean;
  /**
   * #313/#481 — the filing-TRANSPORT axis, orthogonal to `isPortalUpload`. True when the
   * jurisdiction files by EMAIL rather than a portal upload (North Carolina: the `.cfd` is emailed
   * to the NCSBE inbox). NC is BOTH `isPortalUpload` (upload ≠ file, like AL/VA) AND `isEmailFiling`,
   * so a surface that must say "email your report to NCSBE" instead of "upload it to the portal"
   * keys off THIS flag, not `isPortalUpload`. Now the projection of the SERVED
   * `profile.filingTransport === 'email'` (#478) — no longer an FE `isEmailFilingFor(stateCode)`
   * switch — so a future email-filing state is handled without a hand edit. False for AL/VA/LA and
   * federal.
   */
  isEmailFiling: boolean;
  /**
   * #481 — the served filing-TRANSPORT axis verbatim (`profile.filingTransport`, #478): `portal`
   * (AL/VA/LA upload to a named web portal), `email` (NC → NCSBE inbox), or `inSystem` (federal
   * download-and-self-upload). Null while the profile loads or for a rules-only ruleset that cannot
   * file. The single served signal filing surfaces branch on instead of re-deriving transport from a
   * raw state code; `isEmailFiling` is its `=== 'email'` projection.
   */
  filingTransport: FilingTransport | null;
  /**
   * #482 — the filing profile's `certifyAtGenerate` axis. TRUE only for federal (the treasurer's
   * certification embeds in the generated `.fec` at generate time). Orthogonal to `isPortalUpload`:
   * federal is BOTH `isPortalUpload` (upload ≠ file, download-and-self-upload to efo.fec.gov) AND
   * `certifyAtGenerate`. Shared close/ledger copy surfaces read THIS to stay jurisdiction-neutral for
   * federal — it has no nameable state portal, and the FEC-copy discipline forbids naming "FEC" — so
   * `portalNameFor('federal')`'s generic "state portal" default never leaks. FALSE for AL/VA/NC/LA and
   * before the profile loads.
   */
  certifyAtGenerate: boolean;
  /** Friendly jurisdiction name (e.g. "Alabama"); null for federal / unresolved. */
  stateDisplayName: string | null;
  /**
   * Jurisdiction portal name (e.g. "FCPA portal", "COMET portal", "NCSBE"). #481 — the SERVED
   * `profile.portalName` (#478) is the source of truth for the active campaign; the FE
   * `portalNameFor(rulesetCode)` switch is only a fallback for the loading window and for
   * transports with no named destination (federal `inSystem`, rules-only), where the name is
   * neutralised in copy anyway.
   */
  portalName: string;
  /** The resolved ruleset code (state code); null until settings load. */
  rulesetCode: string | null;
  /**
   * #571 — the jurisdiction's SERVED amendment model (ADR-021 `profile.amendmentModel`), read
   * verbatim off the compliance profile instead of re-derived from the default builder format.
   * `PortalOnly` = append-only delta (AL); `SelfContainedUpload` = whole-report re-upload (LA/VA);
   * `ElectronicResubmit` = federal in-system resubmit. Null until the profile loads or for a
   * rules-only ruleset. Surfaces the distinction the rapid-report "mark reported" copy needs: on a
   * whole-report jurisdiction (anything but `PortalOnly`) the gift is SUPPLEMENTAL (it also stays on
   * the periodic report), so marking the rapid reported does NOT remove it from any export — the
   * opposite of AL's append-only "won't be re-uploaded" contract.
   */
  amendmentModel: AmendmentModel | null;
}

/**
 * The slice of the filing model the destination-naming copy helpers read — the three fields that
 * name (or neutralize) where the treasurer files. `certifyAtGenerate` is optional so a caller that
 * doesn't carry the federal axis (a plain portal-upload model literal) still satisfies it; the full
 * {@link PortalUploadModel} always supplies it. Shared here so `filingDestinationPhrase`,
 * `rowLockCopy`, and `markReportedElsewhereCopy` can't drift on the shape they accept.
 */
export type FilingDestinationModel =
  Pick<PortalUploadModel, 'isPortalUpload' | 'stateDisplayName' | 'portalName'> & {
    certifyAtGenerate?: boolean;
    /**
     * #860 — the served filing-TRANSPORT axis (`profile.filingTransport`). Optional so a caller that
     * carries only the upload-model slice still satisfies the shape; the full {@link PortalUploadModel}
     * supplies it. It lets the phrase name a NON-portal (email) destination — NC → "the North Carolina
     * NCSBE", MS → "the Mississippi Secretary of State" / "the Mississippi Circuit Clerk" — instead of
     * keying only on `uploadIsFiling`, so an email filer never collapses to the "portal" word.
     */
    filingTransport?: FilingTransport | null;
  };

/**
 * The single source of the "name the filing destination" phrase — the canonical
 * construction `the {state} {destination}` for a portal-upload jurisdiction (AL →
 * "the Alabama FCPA portal", VA → "the Virginia COMET portal") OR an email-transport
 * jurisdiction (NC → "the North Carolina NCSBE"; MS → "the Mississippi Secretary of
 * State" / "the Mississippi Circuit Clerk"), dropping the state name when the
 * jurisdiction has none. Keyed on the served TRANSPORT axis, not `uploadIsFiling`
 * alone, so an email filer names its receiving office rather than collapsing to a
 * "portal" word. It stays jurisdiction-neutral ("the filing portal") only where there
 * is no nameable destination on a shared surface: a rules-only/loading model, OR
 * federal (`certifyAtGenerate`) — federal files at efo.fec.gov, `portalName` falls
 * back to the generic "state portal" there (wrong for federal), and the FEC-copy
 * discipline forbids naming "FEC" here. Every surface that names where the treasurer
 * files (mark-reported carve-out, receipt-ID placeholder, export upload-gate banner,
 * the report-detail generate steps) derives from this so none re-hardcodes "the
 * Alabama FCPA portal" or leaks "state portal" to federal.
 */
export function filingDestinationPhrase(model: FilingDestinationModel): string {
  // Neutral only when there is no nameable destination: federal (certifyAtGenerate) always, and a
  // model that is neither a portal upload nor an email transport (a rules-only / still-loading
  // profile). An email transport HAS a named receiving office, so it never collapses to "portal".
  if (model.certifyAtGenerate || (!model.isPortalUpload && model.filingTransport !== 'email')) {
    return 'the filing portal';
  }
  return model.stateDisplayName
    ? `the ${model.stateDisplayName} ${model.portalName}`
    : `the ${model.portalName}`;
}

const FILING_TRANSPORTS: readonly FilingTransport[] = ['portal', 'email', 'inSystem', 'agentEntry'];

function knownFilingTransport(served: string | null | undefined): FilingTransport | null {
  return FILING_TRANSPORTS.find((t) => t === served) ?? null;
}

export function usePortalUploadModel(): PortalUploadModel {
  const settings = readySettings(useFinanceSettings());
  const profile = readyProfile(useComplianceProfile());
  const rulesetCode = settings?.rulesetCode ?? null;

  return {
    // Strict `=== false`: an undefined profile (loading) or a null `uploadIsFiling`
    // (rules-only ruleset, no builder) is NOT a portal upload, so the boundary claim
    // stays off until the serving model actually resolves to "upload ≠ file".
    isPortalUpload: profile?.uploadIsFiling === false,
    // #481 — the email verdict is the served transport axis (#478), not an FE `isEmailFilingFor`
    // switch. A null/undefined transport (loading / rules-only / portal / inSystem) is not email.
    isEmailFiling: profile?.filingTransport === 'email',
    filingTransport: knownFilingTransport(profile?.filingTransport),
    // #482 — federal (download-and-self-upload) is the only certifyAtGenerate jurisdiction. Strict
    // `=== true`: a null/undefined profile (loading) or a portal-upload jurisdiction (VA/NC/LA) is not
    // certify-at-generate, so shared surfaces name their portal as usual and only federal stays neutral.
    certifyAtGenerate: profile?.certifyAtGenerate === true,
    stateDisplayName: profile?.stateDisplayName ?? null,
    // #481 — prefer the SERVED portal name (#478); the FE `portalNameFor(rulesetCode)` switch is the
    // fallback only while the profile loads or when the transport has no named destination (federal
    // inSystem / rules-only both serve null), where the name is neutralised in copy anyway.
    portalName: profile?.portalName ?? portalNameFor(rulesetCode),
    rulesetCode,
    // #571 — the served amendment-model axis, verbatim. Null until the profile loads (or for a
    // rules-only ruleset), so an unresolved jurisdiction defaults the rapid-mark copy to the
    // conservative append-only wording (never the supplemental reassurance).
    amendmentModel: profile?.amendmentModel ?? null,
  };
}
